import { Helmet } from 'react-helmet-async';
import { BRAND } from '../../config/brand';
import { Link, useSearchParams } from 'react-router-dom';
import { useWishlist } from '../../context/WishlistContext';
import Layout from '../../components/common/Layout/Layout';
import ProductCard from '../../components/product/ProductCard/ProductCard';
import { IconArrowLeft, IconArrowRight } from '../../components/common/Icons/Icons';
import './Account.css';

const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME = BRAND?.name || 'EVORA';

function AccountWishlist() {
  const { wishlist, loading, error, refreshWishlist, wishlistCount } = useWishlist();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const PER_PAGE = 20;

  const total = wishlistCount || wishlist.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const startIndex = (page - 1) * PER_PAGE;
  const endIndex = startIndex + PER_PAGE;
  const pageItems = wishlist.slice(startIndex, endIndex);

  const handlePageChange = (nextPage) => {
    const target = Math.max(1, Math.min(nextPage, totalPages));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (target === 1) {
        next.delete('page');
      } else {
        next.set('page', String(target));
      }
      return next;
    });
  };

  return (
    <>
      <Helmet>
        <title>Saved Pieces | {BRAND_NAME}</title>
        <meta name="description" content="View your saved pieces and wishlist items." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={`${SITE_URL}/account/wishlist`} />
      </Helmet>
      
      <Layout showFooter={true} showBottomTab={true}>
        <div className="account-page">
        <div className="account-container">
          <div className="account-header">
            <Link to="/account" className="account-back"><IconArrowLeft size={14} /> Back to Account</Link>
            <h1 className="account-title">Saved Pieces</h1>
            {total > 0 && (
              <p className="account-subtitle">
                {total} item{total !== 1 ? 's' : ''} · Page {page} of {totalPages}
              </p>
            )}
            {total === 0 && (
              <p className="account-subtitle">Your wishlist items</p>
            )}
          </div>

          {error && !loading ? (
            <div className="account-error" role="alert">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="account-error__icon" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p className="account-error__message">{error}</p>
              <button
                onClick={() => refreshWishlist(0)}
                className="account-error__retry btn-primary"
                aria-label="Retry loading wishlist"
              >
                Retry
              </button>
            </div>
          ) : loading ? (
            <div className="account-loading">
              <div className="account-loading__skeleton">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="account-loading__skeleton-item">
                    <div className="account-loading__skeleton-line shimmer" />
                    <div className="account-loading__skeleton-line shimmer" />
                    <div className="account-loading__skeleton-line shimmer" />
                  </div>
                ))}
              </div>
            </div>
          ) : pageItems.length > 0 ? (
            <div className="account-wishlist-grid">
              {pageItems.map((product) => (
                <ProductCard key={product._id || product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="account-empty">
              <p>Your wishlist is empty</p>
              <Link to="/shop" className="account-empty__link">
                Start Shopping <IconArrowRight size={14} />
              </Link>
            </div>
          )}

          {!loading && !error && totalPages > 1 && (
            <nav className="account-pagination" aria-label="Wishlist pages">
              <button
                type="button"
                className="account-pagination__btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span className="account-pagination__info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="account-pagination__btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </div>
      </Layout>
    </>
  );
}

export default AccountWishlist;
